<template>
  <section class="bg-mm-mmmorelight ">
    <div class="w-full grid gap-0 bg-mm-mmdark lg:grid-cols-[2fr_1fr]">
      <!-- FORM -->
      <div class="p-9">
        <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-10">{{ t('titles.login') }}</h2>
        <!-- ROL-KEUZE SCHERM: getoond na inloggen als gebruiker meerdere rollen heeft -->
        <div v-if="authChecked && loggedIn && showRolePicker" class="space-y-6 max-w-[520px]" aria-live="polite">
          <h3 class="text-[22px] font-black italic uppercase text-[#111]">{{ tOr('rolePicker.title', 'Met welke rol wil je inloggen?') }}</h3>
          <p class="text-[15px] text-[#555]">{{ tOr('rolePicker.description', 'Je account heeft meerdere rollen. Kies hieronder hoe je wilt inloggen. Je kunt later altijd wisselen via je account.') }}</p>
          <div class="flex flex-col gap-3">
            <button
              v-for="role in pendingRoles"
              :key="role"
              type="button"
              @click="selectRole(role)"
              :disabled="rolePickerLoading"
              class="w-full h-14 border-2 border-black bg-white text-black italic uppercase font-black text-[16px] hover:bg-black hover:text-white transition-colors disabled:opacity-60"
            >
              {{ roleLabel(role) }}
            </button>
          </div>
          <MmFlashMessage v-if="rolePickerError" :message="rolePickerError" type="error" @dismiss="rolePickerError = ''" />
        </div>

        <form v-if="authChecked && !loggedIn" @submit.prevent="handleLogin" class="space-y-5 max-w-[760px]" aria-live="polite">
          <!-- Form-level messages -->
          <MmFlashMessage
            v-if="errorMessage"
            :message="errorMessage"
            type="error"
            @dismiss="errorMessage = ''"
          />
          <!-- Username -->
          <div class="max-w-[520px]">
            <input id="mm-username" ref="usernameInput" v-model="username" type="text" autocomplete="username" required
                   :placeholder="t('placeholders.usernameOrEmail')"
                   class="w-full h-12 bg-transparent px-1 border-0 border-b-2 border-[#d4cfc5] text-black placeholder:text-[#8c857a]
                  focus:outline-none focus:border-black" />
          </div>

          <!-- Password -->
          <div class="max-w-[520px]">
            <div class="relative">
              <input id="mm-password" ref="passwordInput" v-model="password" :type="showPassword ? 'text' : 'password'"
                     autocomplete="current-password" required
                     :placeholder="t('placeholders.password')"
                     @keydown="checkCapsLock" @keyup="checkCapsLock"
                     class="w-full h-12 bg-transparent px-1 pr-12 border-0 border-b-2 border-[#d4cfc5] text-black placeholder:text-[#8c857a]
                    focus:outline-none focus:border-black" />
              <button type="button" @click="togglePassword" @mousedown.prevent @touchstart.prevent
                      class="absolute inset-y-0 right-0 px-3 grid place-items-center text-[#666] hover:text-[#111] focus:outline-none"
                      :title="showPassword ? t('aria.hidePassword') : t('aria.showPassword')"
                      :aria-label="showPassword ? t('aria.hidePassword') : t('aria.showPassword')"
                      :aria-pressed="showPassword">
                <svg v-if="showPassword" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.77 21.77 0 0 1 5.06-5.94"/>
                  <path d="M1 1l22 22"/>
                  <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24"/>
                </svg>
              </button>
            </div>
            <div v-if="capsLockOn" class="mt-1 text-[13px] font-semibold text-mm-orange">
              ⚠️ {{ t('messages.capsLockOn') }}
            </div>
          </div>

          <!-- Cloudflare Turnstile -->
          <div class="max-w-[520px]">
            <div v-if="turnstileEnabled" class="border border-[#e5dfd3] rounded">
              <div ref="turnstile" class="mm-turnstile" />
              <p v-if="turnstileError" class="mt-2 text-[13px] text-[#b91c1c]">{{ turnstileError }}</p>
            </div>
            <div v-else class="flex items-center justify-between border border-[#e5dfd3] rounded bg-white p-3">
              <div class="flex items-center gap-2">
                <div class="w-5 h-5 grid place-items-center bg-[#55b36a] text-white text-[12px] font-bold rounded-sm">✓</div>
                <span class="text-[15px] text-[#333]">{{ t('messages.verificationDisabled') }}</span>
              </div>
              <span class="text-[11px] text-[#777]">TURNSTILE</span>
            </div>
          </div>

          <!-- Submit -->
          <button type="submit" :disabled="isLoading || !formValid"
                  class="w-full max-w-[520px] h-12 bg-black text-white italic uppercase font-semibold tracking-wide
                 disabled:opacity-60">
            <span v-if="isLoading">{{ t('buttons.login') }}…</span><span v-else>{{ t('buttons.login') }}</span>
          </button>

          <!-- Forgot (losse link, geen witte box) -->
          <div class="w-full max-w-[520px] text-left -mt-2">
            <button type="button" @click="handleForgotPassword"
                    class="block text-left p-0 text-[15px] text-[#111] underline italic hover:text-black bg-transparent">
              {{ t('buttons.forgotPassword') }}
            </button>
          </div>

          <!-- Extra: Registreren link ook hier in het formulier -->
          <div class="w-full max-w-[520px] text-left -mt-1">
            <button type="button" @click="handleSignup"
                    class="block text-left p-0 text-[15px] text-[#111] underline italic hover:text-black bg-transparent">
              Nog geen account? {{ t('titles.register') }}
            </button>
          </div>
        </form>
        <!-- Initial state while sessiestatus wordt gecontroleerd -->
        <div v-else class="max-w-[520px] text-[14px] text-[#666]">
          {{ t('messages.loading') }}
        </div>
      </div>

      <!-- INFO PANEL -->
      <aside v-if="authChecked && !loggedIn" class="bg-gradient-to-br from-[#EA6730] to-[#F7941F] text-white p-9 flex flex-col gap-8">
        <div>
          <h3 class="text-[26px] text-white italic uppercase mb-6 leading-tight">
            <span class="whitespace-pre-line">{{ t('marketing.pitchTitle') }}</span>
          </h3>
          <ul class="space-y-1 text-[20px]">
            <li class="flex items-start gap-1">
              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp1') }}</span>
            </li>
            <li class="flex items-start gap-1">
              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp2') }}</span>
            </li>
            <li class="flex items-start gap-1">
              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp3') }}</span>
            </li>
            <li class="flex items-start gap-1">
              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp4') }}</span>
            </li>
          </ul>
        </div>
        <button @click="handleSignup"
                class="mt-4 w-full bg-white text-black italic uppercase font-extrabold h-12">
          {{ t('titles.register') }}
        </button>
      </aside>
    </div>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import Turnstile from '../lib/turnstile.js'
import { t as i18nT } from '../i18n'
import MmFlashMessage from './MmFlashMessage.vue'
export default {
  name: 'MmLogin',
  emits: ['forgotPassword', 'requestSignup', 'requestChangePassword', 'navigate'],
  components: { MmFlashMessage },
  data() {
    return {
      t: i18nT,
      username: '',
      password: '',
      showPassword: false,
      capsLockOn: false,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      loggedIn: false,
      currentUser: null,
      authChecked: false,
      // Turnstile
      turnstileEnabled: !!(Turnstile && Turnstile.isEnabled && Turnstile.isEnabled()),
      turnstileToken: '',
      turnstileWidgetId: null,
      turnstileError: '',
      // Rol-keuze
      showRolePicker: false,
      pendingRoles: [],
      rolePickerLoading: false,
      rolePickerError: '',
    };
  },
  mounted() {
    if (this.turnstileEnabled) {
      this.initTurnstile();
    }
    this.api = new WpApiClient();
    // Probeer huidige gebruiker op te halen om direct status te tonen
    this.fetchCurrentUser();
    },
  computed: {
    formValid(){
      const usernameOk = !!String(this.username||'').trim();
      const passwordOk = !!this.password;
      // UX: maak de knop niet afhankelijk van Turnstile-token, die kan vertraagd binnenkomen.
      // We valideren de Turnstile op submit en scrollen dan naar de widget indien nodig.
      return usernameOk && passwordOk;
    }
  },
  methods: {
    t(key){ return i18nT(key) },
    // Vertaling met veilige fallback: als de i18n sleutel niet bestaat en als key wordt teruggegeven,
    // toon dan de aangeleverde fallbacktekst.
    tOr(key, fallback){
      const v = i18nT(key)
      return (v && v !== key) ? v : fallback
    },
    async initTurnstile() {
      this.turnstileError = '';
      if (!Turnstile.isEnabled()) { this.turnstileEnabled = false; return; }
      await Turnstile.waitForApi();
      if (!(window.turnstile && this.$refs.turnstile)) return;
      try {
        this.turnstileWidgetId = Turnstile.render(this.$refs.turnstile, {
          onToken: (token) => { this.turnstileToken = token; this.turnstileError = ''; },
          onError: () => { this.turnstileToken = ''; this.turnstileError = this.t('messages.turnstileError'); },
          onExpired: () => { this.turnstileToken = ''; this.turnstileError = this.t('messages.turnstileExpired'); },
        });
      } catch (e) {
        this.turnstileError = this.t('messages.turnstileLoadFailed');
      }
    },
    resetTurnstile() {
      try {
        if (this.turnstileWidgetId) {
          Turnstile.reset(this.turnstileWidgetId);
        }
        this.turnstileToken = '';
      } catch (_) {}
    },
    scrollToRef(refName){
      const el = this.$refs[refName];
      if (el && typeof el.scrollIntoView === 'function') {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_) { el.scrollIntoView(); }
      }
      if (el && typeof el.focus === 'function') {
        try { el.focus({ preventScroll: true }); } catch(_) { try { el.focus(); } catch(__) {} }
      }
    },
    findFirstInvalid(){
      if (!String(this.username||'').trim()) return 'usernameInput';
      if (!this.password) return 'passwordInput';
      if (this.turnstileEnabled && !this.turnstileToken) return 'turnstile';
      return '';
    },
    async handleLogin() {
      // Client-side UX: scroll to first invalid and stop; no bottom client-side error
      if (!this.formValid) {
        const ref = this.findFirstInvalid();
        if (ref) this.scrollToRef(ref);
        return;
      }
      // Turnstile: afdwingen bij submit i.p.v. de knop te blokkeren
      if (this.turnstileEnabled && !this.turnstileToken) {
        // Toon duidelijke melding en focus de widget
        this.turnstileError = this.tOr('messages.turnstileRequired', 'Verificatie vereist. Bevestig de Turnstile controle.');
        this.scrollToRef('turnstile');
        return;
      }
      this.isLoading = true; this.errorMessage = '';
      try {
        let result = await this.api.login({
          username: this.username,
          password: this.password,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined,
          turnstileToken: (this.turnstileEnabled && this.turnstileToken) ? this.turnstileToken : undefined,
        });

        // Automatische retry bij nonce error
        if (result && !result.ok && result.error && result.error.includes('nonce')) {
          // Kleine vertraging om de browser de tijd te geven cookies te verwerken
          await new Promise(resolve => setTimeout(resolve, 500));
          const newNonce = await this.api.refreshNonce(true);
          if (newNonce) {
            result = await this.api.login({
              username: this.username,
              password: this.password,
              nonce: newNonce,
              turnstileToken: (this.turnstileEnabled && this.turnstileToken) ? this.turnstileToken : undefined,
            });
          }
        }

        if(result && result.ok){
          this.successMessage = this.t('messages.loginSuccess');
          this.loggedIn = true;
          this.currentUser = (result && result.user) ? result.user : this.currentUser;
          if (!this.currentUser) await this.fetchCurrentUser();

          if (this.currentUser && (this.currentUser.roles || []).includes('administrator')) {
            window.location.href = '/wp-admin';
            return;
          }

          // Toon rol-keuze scherm als gebruiker meerdere rollen heeft
          const switchable = (this.currentUser && this.currentUser.switchableRoles) || [];
          if (switchable.length >= 2) {
            this.pendingRoles = switchable;
            this.showRolePicker = true;
            this.isLoading = false;
            return;
          }

          window.location.href = '/account';
        } else {
          this.errorMessage = (result && result.error) || this.t('messages.loginFailed');
          this.resetTurnstile();
        }
      } catch(e){ this.errorMessage = e?.message || this.t('messages.genericError'); this.resetTurnstile(); }
      this.isLoading=false;
    },
    togglePassword(){
      this.showPassword = !this.showPassword;
      this.$nextTick(() => {
        const el = this.$refs.passwordInput;
        if (el && typeof el.focus === 'function') {
          const pos = el.value ? el.value.length : 0;
          el.focus({ preventScroll: true });
          try { el.setSelectionRange(pos, pos); } catch(_) {}
        }
      });
    },
    checkCapsLock(e){ this.capsLockOn=e.getModifierState&&e.getModifierState('CapsLock'); },
    async handleLogout(){
      try{
        await this.api.logout({ nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined });
      }catch(_){ /* ignore */ }
      // Always redirect to login page, regardless of AJAX result
      window.location.href = '/inloggen';
    },
    roleLabel(role) {
      const map = { coordinator: 'Coördinator', school: 'School', bedrijf: 'Bedrijf' };
      return map[role] || role;
    },
    async selectRole(role) {
      this.rolePickerLoading = true;
      this.rolePickerError = '';
      try {
        const res = await this.api.switchRole({ role, nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined });
        if (res && res.ok) {
          window.location.href = '/account';
        } else {
          this.rolePickerError = (res && res.error) || this.tOr('messages.genericError', 'Er ging iets mis. Probeer later opnieuw.');
        }
      } catch(e) {
        this.rolePickerError = e?.message || this.tOr('messages.genericError', 'Er ging iets mis. Probeer later opnieuw.');
      }
      this.rolePickerLoading = false;
    },
    handleForgotPassword(){ this.$emit('forgotPassword'); },
    handleSignup(){ this.$emit('requestSignup'); },
    handleChangePassword(){ this.$emit('requestChangePassword'); },
    gotoProfile(){
      // Ga naar bewerken op nette URL en toon MmProfile
      try { window.history.replaceState(null, '', '/account/account-wijzigen'); } catch(_) {}
      // Laad het account-dashboard; dit toont de juiste subview o.b.v. URL
      this.$emit('navigate', 'MmAccount');
    },
    async fetchCurrentUser(){
      try{
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000));
        const res = await Promise.race([
          this.api.getCurrentUser({ nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined }),
          timeout
        ]);
        if (res && res.ok && res.user) {
          this.currentUser = res.user; this.loggedIn = true;
          // Als we al ingelogd zijn en we zitten op de loginpagina, ga naar de juiste pagina en toon MmAccount
          this.$nextTick(() => {
            let targetPath = '/account';

            if (this.currentUser && (this.currentUser.roles || []).includes('administrator')) {
              window.location.href = '/wp-admin';
              return;
            }

            window.location.href = targetPath;
          })
        } else {
          // Als de aanroep faalt door een nonce-fout, ververs de nonce direct
          if (res && !res.ok && (res.error === 'invalid_nonce' || (res.error && res.error.includes('nonce')))) {
             await this.api.refreshNonce(false);
          }
          this.loggedIn = false;
        }
      }catch(_){ 
        // Bij een echte exception (netwerkfout o.i.d.), probeer ook de nonce te verversen
        await this.api.refreshNonce(false);
        this.loggedIn = false; 
      }
      finally {
        this.authChecked = true;
        // Focus username veld zodra we weten dat er geen sessie is
        if (!this.loggedIn) {
          this.$nextTick(() => {
            try { this.$refs.usernameInput && this.$refs.usernameInput.focus(); } catch(_){}
            // Init Turnstile pas nadat het formulier (en de ref) daadwerkelijk in de DOM staat
            if (this.turnstileEnabled) {
              this.initTurnstile();
            }
          });
        }
      }
    }
  }
};
</script>